import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, Image, Input, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiOutlineEye, HiOutlinePaintBrush, HiOutlinePhoto, HiOutlineMoon, HiOutlineSun, HiArrowUp } from "react-icons/hi2";
import { useAuth } from "@/auth/AuthContext";
import { useBranding, brandAssetUrl } from "@/auth/BrandingContext";
import AccountService from "@/services/AccountService";
import { toaster } from "@/components/ui/toaster";
import { Field } from "@/components/jobOffer/ui/inputs";
import useChatTheme from "@/pages/chat/hooks/useChatTheme";
import { isHexColor } from "@/pages/chat/hooks/brandColors";
import SectionCard from "./SectionCard";

const DEFAULT_PRIMARY = "#8700DB";
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_BYTES = 1024 * 1024;

const toForm = (b) => ({ primaryColor: b?.primaryColor ?? "", chatTheme: b?.chatTheme ?? "light" });

const LogoUpload = ({ hint, url, busy, disabled, onUpload, onDelete, t }) => {
    const inputRef = useRef(null);
    return (
        <Box w="full">
            <Text color="text.grey" fontSize="xs" mb={3}>{hint}</Text>
            <HStack gap={4} align="center">
                <Box
                    w="160px"
                    h="80px"
                    borderRadius={10}
                    border="1px dashed"
                    borderColor="border.secondary"
                    bg="bg.dark"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                    flexShrink={0}
                >
                    {busy ? <Spinner size="sm" color="brand.400" />
                        : url ? <Image src={url} alt="" maxW="100%" maxH="100%" objectFit="contain" p={2} />
                        : <HiOutlinePhoto size={24} color="#727272" />}
                </Box>
                <VStack align="flex-start" gap={2}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPT}
                        hidden
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) onUpload(file);
                        }}
                    />
                    <Button variant="outlined" size="xs" onClick={() => inputRef.current?.click()} disabled={disabled || busy} type="button">
                        {url ? t("settingsPage.branding.replace") : t("settingsPage.branding.upload")}
                    </Button>
                    {url && (
                        <Button variant="ghost" size="xs" color="red.400" onClick={onDelete} disabled={disabled || busy} type="button">
                            {t("settingsPage.branding.remove")}
                        </Button>
                    )}
                </VStack>
            </HStack>
        </Box>
    );
};

const ChatPreview = ({ primaryColor, chatTheme, logoUrl, companyName, t }) => {
    const colors = useChatTheme(chatTheme === "dark", primaryColor);
    return (
        <Box borderRadius={12} overflow="hidden" border="1px solid" borderColor="border.primary" style={{ background: colors.chatBg }} maxW="420px" w="full">
            <HStack px={4} py={3} justify="space-between" style={{ background: colors.headerBg, borderBottom: `1px solid ${colors.headerBorder}` }}>
                <HStack gap={3}>
                    <Box w={9} h={9} borderRadius="full" overflow="hidden" display="flex" alignItems="center" justifyContent="center"
                         style={{ background: logoUrl ? colors.chatBg : colors.avatarBg, border: logoUrl ? `1px solid ${colors.borderColor}` : "none" }}>
                        {logoUrl ? <Image src={logoUrl} alt="" w="full" h="full" objectFit="contain" p={1} />
                            : <Text fontSize="xs" fontWeight="bold" style={{ color: colors.onAccent }}>AI</Text>}
                    </Box>
                    <VStack gap={0} align="flex-start">
                        <Text fontSize="sm" fontWeight={600} style={{ color: colors.textPrimary }}>{t("settingsPage.branding.previewBot")}</Text>
                        <Text fontSize="xs" style={{ color: colors.textSecondary }}>{companyName || t("settingsPage.branding.previewJob")}</Text>
                    </VStack>
                </HStack>
                <HStack gap={2}>
                    <Box style={{ color: colors.iconColor }}>{chatTheme === "dark" ? <HiOutlineSun size={16} /> : <HiOutlineMoon size={16} />}</Box>
                </HStack>
            </HStack>
            <VStack px={4} py={4} gap={3} align="stretch">
                <Box alignSelf="flex-start" maxW="80%" px={3} py={2} borderRadius="16px" borderBottomLeftRadius="4px"
                     style={{ background: colors.botBubble, color: colors.textPrimary }}>
                    <Text fontSize="sm">{t("settingsPage.branding.previewBotMessage")}</Text>
                </Box>
                <Box alignSelf="flex-end" maxW="80%" px={3} py={2} borderRadius="16px" borderBottomRightRadius="4px"
                     style={{ background: colors.userBubble, color: colors.onAccent }}>
                    <Text fontSize="sm">{t("settingsPage.branding.previewUserMessage")}</Text>
                </Box>
            </VStack>
            <HStack mx={3} mb={3} px={3} py={2} borderRadius={12} justify="space-between" style={{ background: colors.inputBg }}>
                <Text fontSize="sm" style={{ color: colors.textSecondary }}>{t("settingsPage.branding.previewPlaceholder")}</Text>
                <Box w="24px" h="24px" borderRadius="full" display="flex" alignItems="center" justifyContent="center"
                     style={{ background: colors.accent, color: colors.onAccent }}>
                    <HiArrowUp size={12} />
                </Box>
            </HStack>
        </Box>
    );
};

const BrandingSection = () => {
    const { t } = useTranslation("account");
    const { isOwner } = useAuth();
    const { branding, setBranding } = useBranding();

    const [form, setForm] = useState(toForm(branding));
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Logo uploads must not wipe unsaved colour edits.
    useEffect(() => { setForm(toForm(branding)); }, [branding?.primaryColor, branding?.chatTheme]); // eslint-disable-line react-hooks/exhaustive-deps

    if (branding === null) {
        return (
            <HStack w="full" py={8} justify="center">
                <Spinner color="brand.400" size="md" />
            </HStack>
        );
    }

    const saved = toForm(branding);
    const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
    const colorValid = form.primaryColor === "" || isHexColor(form.primaryColor);
    const disabled = !isOwner || isSaving;
    const logoUrl = brandAssetUrl(branding.logoUrl);
    const previewColor = isHexColor(form.primaryColor) ? form.primaryColor : null;

    const errorMessage = (err, fallbackKey) => err?.response?.data?.error ?? t(fallbackKey);

    const handleUpload = async (file) => {
        if (file.size > MAX_BYTES) {
            toaster.create({ type: "error", title: t("settingsPage.branding.fileTooLarge") });
            return;
        }
        setIsUploading(true);
        try {
            setBranding(await AccountService.uploadBrandingLogo(file));
            toaster.create({ type: "success", title: t("settingsPage.branding.toastUploadSuccess") });
        } catch (err) {
            toaster.create({ type: "error", title: errorMessage(err, "settingsPage.branding.toastUploadError") });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async () => {
        setIsUploading(true);
        try {
            setBranding(await AccountService.deleteBrandingLogo());
        } catch (err) {
            toaster.create({ type: "error", title: errorMessage(err, "settingsPage.branding.toastSaveError") });
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            setBranding(await AccountService.updateBranding({
                primaryColor: form.primaryColor || null,
                chatTheme: form.chatTheme,
            }));
            toaster.create({ type: "success", title: t("settingsPage.branding.toastSaveSuccess") });
        } catch (err) {
            toaster.create({ type: "error", title: errorMessage(err, "settingsPage.branding.toastSaveError") });
        } finally {
            setIsSaving(false);
        }
    };

    const renderThemeOption = (value, icon, label) => {
        const active = form.chatTheme === value;
        return (
            <Button
                type="button"
                size="sm"
                key={value}
                variant="outlined"
                disabled={disabled}
                onClick={() => setForm((f) => ({ ...f, chatTheme: value }))}
                borderColor={active ? "var(--brand-accent, #a855f7)" : "border.button"}
                bg={active ? "bg.highlight" : "transparent"}
                paddingX={4}
            >
                {icon} {label}
            </Button>
        );
    };

    return (
        <VStack w="full" gap={4} align="flex-start">
            {!isOwner && <Text color="text.grey" fontSize="sm">{t("settingsPage.branding.ownerOnly")}</Text>}

            <SectionCard icon={<HiOutlinePhoto />} title={t("settingsPage.branding.assetsTitle")} subtitle={t("settingsPage.branding.assetsSubtitle")}>
                <LogoUpload
                    hint={t("settingsPage.branding.logoHint")}
                    url={logoUrl}
                    busy={isUploading}
                    disabled={!isOwner}
                    onUpload={handleUpload}
                    onDelete={handleDelete}
                    t={t}
                />
            </SectionCard>

            <SectionCard icon={<HiOutlinePaintBrush />} title={t("settingsPage.branding.colorsTitle")} subtitle={t("settingsPage.branding.colorsSubtitle")}>
                <HStack w="full" gap={6} align="flex-start" flexWrap="wrap">
                    <Box flex="1" minW="220px">
                        <Field
                            label={t("settingsPage.branding.primaryLabel")}
                            hint={t("settingsPage.branding.primaryHint")}
                            invalid={!colorValid}
                        >
                            <HStack w="full" gap={2}>
                                <Box
                                    as="label"
                                    w="40px"
                                    h="40px"
                                    flexShrink={0}
                                    borderRadius={8}
                                    border="1px solid"
                                    borderColor="border.primary"
                                    cursor={disabled ? "not-allowed" : "pointer"}
                                    style={{ background: previewColor ?? DEFAULT_PRIMARY }}
                                    position="relative"
                                    overflow="hidden"
                                >
                                    <input
                                        type="color"
                                        value={previewColor ?? DEFAULT_PRIMARY}
                                        disabled={disabled}
                                        onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value.toUpperCase() }))}
                                        style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "inherit" }}
                                        aria-label={t("settingsPage.branding.primaryLabel")}
                                    />
                                </Box>
                                <Input
                                    value={form.primaryColor}
                                    placeholder={DEFAULT_PRIMARY}
                                    maxLength={7}
                                    disabled={disabled}
                                    onChange={(e) => setForm((f) => ({ ...f, primaryColor: e.target.value.trim() }))}
                                    bg="bg.dark"
                                    borderColor={colorValid ? "border.primary" : "red.400"}
                                    color="gray.900"
                                    fontFamily="mono"
                                />
                                {form.primaryColor && (
                                    <Button variant="ghost" size="sm" color="text.grey" disabled={disabled} type="button"
                                            onClick={() => setForm((f) => ({ ...f, primaryColor: "" }))}>
                                        {t("settingsPage.branding.resetColor")}
                                    </Button>
                                )}
                            </HStack>
                        </Field>
                        {!colorValid && <Text color="red.400" fontSize="xs" mt={1}>{t("settingsPage.branding.invalidColor")}</Text>}
                    </Box>
                    <Box flex="1" minW="220px">
                        <Field label={t("settingsPage.branding.chatThemeLabel")} hint={t("settingsPage.branding.chatThemeHint")}>
                            <HStack gap={2}>
                                {renderThemeOption("light", <HiOutlineSun />, t("settingsPage.branding.themeLight"))}
                                {renderThemeOption("dark", <HiOutlineMoon />, t("settingsPage.branding.themeDark"))}
                            </HStack>
                        </Field>
                    </Box>
                </HStack>
            </SectionCard>

            <SectionCard icon={<HiOutlineEye />} title={t("settingsPage.branding.previewTitle")} subtitle={t("settingsPage.branding.previewSubtitle")}>
                <ChatPreview
                    primaryColor={previewColor}
                    chatTheme={form.chatTheme}
                    logoUrl={logoUrl}
                    companyName={branding.companyName}
                    t={t}
                />
            </SectionCard>

            {isOwner && (
                <HStack w="full" justify="flex-end" gap={2}>
                    <Button variant="outlined" size="sm" onClick={() => setForm(saved)} disabled={!isDirty || isSaving} type="button">
                        {t("settingsPage.cancelButton")}
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving} disabled={!colorValid} type="button">
                        {t("settingsPage.saveButton")}
                    </Button>
                </HStack>
            )}
        </VStack>
    );
};

export default BrandingSection;
